import React, { Fragment, useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Bars3Icon, XMarkIcon, ChevronDownIcon } from '@heroicons/react/24/outline'
import clsx from 'clsx'
import { useLanguage } from '../../contexts/LanguageContext'
import ServicesNavDropdown from './ServicesNavDropdown'
import { goToHomePage } from '../../lib/goToHomePage'

const headerLogo = '/img/sosvor-header-logo.webp'
const MOBILE_DRAWER_WIDTH = 'w-[85%]'
const MOBILE_OVERLAY_WIDTH = 'w-[15%]'

function canUseHoverMenu() {
  return typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches
}

const NAV_LINKS = [
  { nameKey: 'nav.about', href: '/sobre' },
  { nameKey: 'nav.recruitment', href: '/carreira' },
  { nameKey: 'nav.news', href: '/blog' },
]

function linkActive(pathname, href) {
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(`${href}/`)
}

function servicesActive(pathname) {
  return pathname === '/servicos' || pathname.startsWith('/servicos/')
}

function LangSwitch({ className = '', variant = 'light' }) {
  const { locale, setLocale, t } = useLanguage()
  const codes = ['pt', 'en', 'fr']
  const isDark = variant === 'dark'
  return (
    <div
      className={clsx(
        'flex items-center justify-center gap-0 text-[11px] font-semibold uppercase tracking-[0.14em]',
        className
      )}
      role="group"
      aria-label={t('lang.switch')}
    >
      {codes.map((code, i) => (
        <Fragment key={code}>
          {i > 0 && (
            <span className={clsx('px-2 sm:px-3', isDark ? 'text-white/25' : 'text-slate-300')} aria-hidden>
              |
            </span>
          )}
          <button
            type="button"
            onClick={() => setLocale(code)}
            aria-pressed={locale === code}
            className={clsx(
              'rounded-none px-1 py-1 outline-none transition-colors transition-transform duration-150 active:scale-95',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/70 focus-visible:ring-offset-2',
              isDark
                ? locale === code
                  ? 'text-white'
                  : 'text-white/55 hover:text-white'
                : locale === code
                  ? 'text-brand-navy'
                  : 'text-slate-500 hover:text-brand-navy',
              isDark && 'focus-visible:ring-offset-brand-navy'
            )}
          >
            {t(`lang.${code}`)}
          </button>
        </Fragment>
      ))}
    </div>
  )
}

function mobileNavLinkClass(active) {
  return clsx(
    'block rounded-md py-2.5 text-[12px] font-semibold transition-colors',
    active ? 'text-white' : 'text-white/70 hover:text-white'
  )
}

function NavLink({ item, pathname, t }) {
  const active = linkActive(pathname, item.href)
  return (
    <Link
      to={item.href}
      className={clsx(
        'relative py-2 text-[13px] font-semibold transition-colors',
        active ? 'text-brand-navy' : 'text-slate-600 hover:text-brand-navy'
      )}
    >
      {t(item.nameKey)}
      {active && (
        <span className="absolute inset-x-0 -bottom-[17px] h-0.5 bg-brand-navy" aria-hidden />
      )}
    </Link>
  )
}

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [servicesOpen, setServicesOpen] = useState(false)
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false)
  const location = useLocation()
  const { t } = useLanguage()

  const closeMenus = () => {
    setIsOpen(false)
    setMobileServicesOpen(false)
    setServicesOpen(false)
  }

  const openMobileMenu = () => {
    setServicesOpen(false)
    setMobileServicesOpen(false)
    setIsOpen(true)
  }

  useEffect(() => {
    closeMenus()
  }, [location.pathname])

  useEffect(() => {
    closeMenus()

    const handlePageShow = (event) => {
      if (event.persisted) closeMenus()
    }

    const mediaQuery = window.matchMedia('(min-width: 1024px)')
    const handleDesktop = () => {
      if (mediaQuery.matches) closeMenus()
    }

    window.addEventListener('pageshow', handlePageShow)
    mediaQuery.addEventListener('change', handleDesktop)

    return () => {
      window.removeEventListener('pageshow', handlePageShow)
      mediaQuery.removeEventListener('change', handleDesktop)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [isOpen])

  const svcActive = servicesActive(location.pathname)

  const handleLogoClick = (event) => {
    closeMenus()
    goToHomePage(event)
  }

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-rule bg-white/95 backdrop-blur-sm">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="relative flex h-16 items-center justify-between">
          <a
            href="/"
            aria-label="SOSVOR — Início"
            className="flex items-center shrink-0"
            onClick={handleLogoClick}
            onMouseEnter={() => setServicesOpen(false)}
          >
            <img
              src={headerLogo}
              alt="SOSVOR"
              width={220}
              height={84}
              className="h-10 w-auto object-contain md:h-11"
            />
          </a>

          <div className="hidden lg:absolute lg:left-1/2 lg:flex lg:-translate-x-1/2 lg:items-center lg:gap-8">
            <div className="flex items-center gap-8">
              <div onMouseEnter={() => setServicesOpen(false)}>
                <NavLink item={NAV_LINKS[0]} pathname={location.pathname} t={t} />
              </div>

              <button
                type="button"
                className={clsx(
                  'relative py-2 text-[13px] font-semibold transition-colors',
                  svcActive || servicesOpen
                    ? 'text-brand-navy'
                    : 'text-slate-600 hover:text-brand-navy'
                )}
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                onMouseEnter={() => {
                  if (canUseHoverMenu()) setServicesOpen(true)
                }}
              >
                {t('nav.services')}
                {(svcActive || servicesOpen) && (
                  <span className="absolute inset-x-0 -bottom-[17px] h-0.5 bg-brand-navy" aria-hidden />
                )}
              </button>

              {NAV_LINKS.slice(1).map((item) => (
                <div key={item.nameKey} onMouseEnter={() => setServicesOpen(false)}>
                  <NavLink item={item} pathname={location.pathname} t={t} />
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex lg:items-center lg:gap-10" onMouseEnter={() => setServicesOpen(false)}>
            <LangSwitch className="text-slate-600" />
            <Link
              to="/contato"
              className="inline-flex items-center bg-brand-navy px-5 py-2.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-[#0c1a30]"
            >
              {t('nav.contact')}
            </Link>
          </div>

          <div className="flex items-center gap-3 lg:hidden">
            {!isOpen && <LangSwitch className="text-slate-600" />}
            <button
              type="button"
              onClick={() => (isOpen ? closeMenus() : openMobileMenu())}
              className="p-2 text-brand-navy transition-colors hover:bg-slate-100 rounded-md outline-none active:scale-95 focus-visible:ring-2 focus-visible:ring-brand-blue/70 focus-visible:ring-offset-2"
              aria-label={isOpen ? t('nav.closeMenu') : t('nav.openMenu')}
              aria-expanded={isOpen}
            >
              {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
            </button>
          </div>
        </div>

      </nav>

      {isOpen &&
        createPortal(
          <AnimatePresence mode="wait">
            <>
              <motion.button
                type="button"
                key="mobile-menu-overlay"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={clsx(
                  'fixed inset-y-0 left-0 z-[200] bg-black/45 lg:hidden',
                  MOBILE_OVERLAY_WIDTH
                )}
                onClick={closeMenus}
                aria-label={t('nav.closeMenu')}
              />

              <motion.aside
                key="mobile-menu-drawer"
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
                className={clsx(
                  'mv-pillars-band--menu-drawer fixed inset-y-0 right-0 z-[210] flex flex-col overflow-hidden border-l border-white/10 text-white shadow-[-12px_0_40px_rgba(10,25,47,0.45)] lg:hidden',
                  MOBILE_DRAWER_WIDTH
                )}
                aria-label={t('nav.menuLabel')}
              >
                <div className="relative z-10 flex min-h-0 flex-1 flex-col">
                <div className="flex items-center justify-end border-b border-white/10 px-6 py-4">
                  <button
                    type="button"
                    onClick={closeMenus}
                    className="rounded-md p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                    aria-label={t('nav.closeMenu')}
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-6">
                  <nav className="space-y-2">
                    <Link
                      to="/sobre"
                      onClick={closeMenus}
                      className={mobileNavLinkClass(linkActive(location.pathname, '/sobre'))}
                    >
                      {t('nav.about')}
                    </Link>

                    <div>
                      <button
                        type="button"
                        onClick={() => setMobileServicesOpen((open) => !open)}
                        className={clsx(
                          'flex w-full items-center justify-between rounded-md py-2.5 text-[12px] font-semibold transition-colors',
                          mobileServicesOpen ? 'text-white' : 'text-white/70 hover:text-white'
                        )}
                        aria-expanded={mobileServicesOpen}
                      >
                        {t('nav.services')}
                        <ChevronDownIcon
                          className={clsx('h-4 w-4 transition-transform', mobileServicesOpen && 'rotate-180')}
                          aria-hidden
                        />
                      </button>
                      <AnimatePresence>
                        {mobileServicesOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.2 }}
                            className="pt-2"
                          >
                            <ServicesNavDropdown embedded onLinkClick={closeMenus} />
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {NAV_LINKS.slice(1).map((item) => {
                      const active = linkActive(location.pathname, item.href)
                      return (
                        <Link
                          key={item.nameKey}
                          to={item.href}
                          onClick={closeMenus}
                          className={mobileNavLinkClass(active)}
                        >
                          {t(item.nameKey)}
                        </Link>
                      )
                    })}
                  </nav>
                </div>

                <div className="space-y-5 border-t border-white/10 px-6 py-6">
                  <LangSwitch variant="dark" className="text-[10px]" />
                  <Link
                    to="/contato"
                    onClick={closeMenus}
                    className="block border border-white/20 bg-white/10 px-5 py-3 text-center text-[11px] font-semibold uppercase tracking-[0.1em] text-white transition-colors hover:bg-white/15"
                  >
                    {t('nav.contact')}
                  </Link>
                </div>
                </div>
              </motion.aside>
            </>
          </AnimatePresence>,
          document.body
        )}

      <AnimatePresence>
        {servicesOpen && (
          <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 4 }}
            transition={{ duration: 0.1, ease: 'easeOut' }}
            className="fixed inset-x-0 top-14 z-50 hidden px-3 pb-5 pt-2 lg:block sm:px-5 lg:px-8"
            onMouseEnter={() => {
              if (canUseHoverMenu()) setServicesOpen(true)
            }}
            onMouseLeave={() => setServicesOpen(false)}
          >
            <div className="mx-auto w-full max-w-[min(100%,calc(100vw-1.5rem))]">
              <ServicesNavDropdown onLinkClick={() => setServicesOpen(false)} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
